import { EnvironmentProviders, Injectable, inject } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import {
  AutoPaymentPlanEventEventTypeMap,
  AutoPaymentPlanWorkflowEvent,
  AutoPaymentPlanWorkflowEventMetaDataMap,
} from './auto-payment-plan-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Per-container tracker for Flow 8b (Autopay). Narrows `TrackWorkflowService` to the
 * auto-payment-plan step enum + metadata map and exposes a small operation-duration helper
 * so SUBMITTED → SUCCEEDED / FAILED triads can stamp `operationDurationMs` without each
 * caller juggling its own `performance.now()` baseline.
 *
 * Per-container scope (`@Injectable()`, no `providedIn`). Wire via
 * `providers: [AutoPaymentPlanTrackService]` on the host container so concurrently-mounted
 * dialogs don't share operation timers.
 */
@Injectable()
export class AutoPaymentPlanTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<AutoPaymentPlanWorkflowEvent, AutoPaymentPlanWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  trackWorkflow<E extends AutoPaymentPlanWorkflowEvent>(step: E, metadata?: AutoPaymentPlanWorkflowEventMetaDataMap[E]): void {
    this.tracker.trackWorkflow(step, metadata);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }
}

export function provideAutoPaymentPlanWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<AutoPaymentPlanWorkflowEvent, AutoPaymentPlanWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.AUTO_PAYMENT_PLAN]: AutoPaymentPlanWorkflowEvent }),
    stepToEventType: AutoPaymentPlanEventEventTypeMap,
  });
}
